import { fingerprintFromRgba } from "./umpireScoreChange";

/**
 * JPEG export that stays quiet when the bitmap is origin-tainted.
 * Playback on storage.googleapis.com draws OVH/GCS clips without CORS, so
 * toBlob throws SecurityError instead of calling back with null.
 */
export function safeCanvasToJpegBlob(canvas, quality = 0.62) {
  if (!canvas || typeof canvas.toBlob !== "function") {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    try {
      canvas.toBlob((blob) => resolve(blob || null), "image/jpeg", quality);
    } catch {
      resolve(null);
    }
  });
}

/**
 * Crop the umpire-desk region from the championship record canvas
 * and pack it as a small JPEG for Gemini score OCR.
 */

export function canvasToUmpireScoreFrame(canvas, { quality = 0.62 } = {}) {
  if (!canvas || !canvas.width || !canvas.height) {
    return Promise.resolve(null);
  }
  const srcW = canvas.width;
  const srcH = canvas.height;
  const sx = 0;
  const sw = Math.max(48, Math.round(srcW * 0.32));
  const sy = Math.round(srcH * 0.26);
  const sh = Math.max(48, Math.round(srcH * 0.42));
  const scale = Math.min(1, 420 / sw);
  const out = document.createElement("canvas");
  out.width = Math.max(32, Math.round(sw * scale));
  out.height = Math.max(32, Math.round(sh * scale));
  const ctx = out.getContext("2d");
  if (!ctx) return Promise.resolve(null);
  ctx.drawImage(canvas, sx, sy, sw, sh, 0, 0, out.width, out.height);
  let gray = null;
  try {
    const pixels = ctx.getImageData(0, 0, out.width, out.height);
    gray = fingerprintFromRgba(pixels.data, pixels.width, pixels.height);
  } catch {
    gray = null;
  }
  return safeCanvasToJpegBlob(out, quality).then((blob) =>
    blob ? { blob, gray } : null
  );
}

export function videoToUmpireScoreFrame(video, opts) {
  if (!video || !video.videoWidth || !video.videoHeight) {
    return Promise.resolve(null);
  }
  const canvas = document.createElement("canvas");
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) return Promise.resolve(null);
  try {
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  } catch {
    return Promise.resolve(null);
  }
  return canvasToUmpireScoreFrame(canvas, opts);
}

export function canvasToUmpireScoreBlob(canvas, opts) {
  return canvasToUmpireScoreFrame(canvas, opts).then((frame) => frame?.blob || null);
}

function clipUnit(n) {
  return Math.round(Math.min(1, Math.max(0, n)) * 10000) / 10000;
}

/** Normalized 0–1 box. Rejects slivers. */
export function normalizeRoi(roi) {
  const x0 = Number(roi?.x);
  const y0 = Number(roi?.y);
  const w0 = Number(roi?.w);
  const h0 = Number(roi?.h);
  if (![x0, y0, w0, h0].every((n) => Number.isFinite(n))) return null;
  const x = clipUnit(x0);
  const y = clipUnit(y0);
  const w = clipUnit(Math.min(1 - x, Math.max(0, w0)));
  const h = clipUnit(Math.min(1 - y, Math.max(0, h0)));
  if (w < 0.02 || h < 0.02) return null;
  return { x, y, w, h };
}

/**
 * JPEG of a normalized region of the current video frame.
 * Returns a raw base64 payload (no data-URL prefix).
 */
/**
 * Crop a normalized ROI from video/canvas into a JPEG blob + gray fingerprint
 * for the live score-camera change gate.
 */
export function canvasRoiToScoreFrame(canvas, roi, { quality = 0.62, maxW = 480 } = {}) {
  const box = normalizeRoi(roi);
  if (!box || !canvas?.width || !canvas?.height) {
    return Promise.resolve(null);
  }
  const sx = Math.round(box.x * canvas.width);
  const sy = Math.round(box.y * canvas.height);
  const sw = Math.max(8, Math.round(box.w * canvas.width));
  const sh = Math.max(8, Math.round(box.h * canvas.height));
  const scale = Math.min(1, maxW / sw);
  const out = document.createElement("canvas");
  out.width = Math.max(16, Math.round(sw * scale));
  out.height = Math.max(16, Math.round(sh * scale));
  const ctx = out.getContext("2d");
  if (!ctx) return Promise.resolve(null);
  try {
    ctx.drawImage(canvas, sx, sy, sw, sh, 0, 0, out.width, out.height);
  } catch {
    return Promise.resolve(null);
  }
  let gray = null;
  try {
    const pixels = ctx.getImageData(0, 0, out.width, out.height);
    gray = fingerprintFromRgba(pixels.data, pixels.width, pixels.height);
  } catch {
    gray = null;
  }
  return safeCanvasToJpegBlob(out, quality).then((blob) =>
    blob ? { blob, gray } : null
  );
}

export function videoRoiToScoreFrame(video, roi, opts) {
  if (!video?.videoWidth || !video?.videoHeight) {
    return Promise.resolve(null);
  }
  const canvas = document.createElement("canvas");
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) return Promise.resolve(null);
  try {
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  } catch {
    return Promise.resolve(null);
  }
  return canvasRoiToScoreFrame(canvas, roi, opts);
}

/** Width of the ROI sample used only for the 32×24 change fingerprint. */
const FINGERPRINT_SAMPLE_MAX_W = 128;
let fingerprintCanvas = null;

function sourceSize(source) {
  return {
    w: Number(source?.videoWidth || source?.width) || 0,
    h: Number(source?.videoHeight || source?.height) || 0,
  };
}

/** Keep a crop inside the frame. A source rect past the edge throws on <video>. */
export function clampSourceRect(sx, sy, sw, sh, w, h) {
  const frameW = Math.max(0, Math.round(Number(w) || 0));
  const frameH = Math.max(0, Math.round(Number(h) || 0));
  let x = Math.round(Number(sx) || 0);
  let y = Math.round(Number(sy) || 0);
  let width = Math.round(Number(sw) || 0);
  let height = Math.round(Number(sh) || 0);
  if (frameW < 1 || frameH < 1 || x >= frameW || y >= frameH) return null;
  if (x < 0) x = 0;
  if (y < 0) y = 0;
  if (x + width > frameW) width = frameW - x;
  if (y + height > frameH) height = frameH - y;
  if (width < 1 || height < 1) return null;
  return { sx: x, sy: y, sw: width, sh: height };
}

function roiSourceRect(box, w, h) {
  return clampSourceRect(
    Math.round(box.x * w),
    Math.round(box.y * h),
    Math.max(8, Math.round(box.w * w)),
    Math.max(8, Math.round(box.h * h)),
    w,
    h
  );
}

function noteRoiFailure(detail, reason) {
  if (detail && typeof detail === "object") detail.reason = reason;
}

/**
 * Per-tick sample for the change gate: draws only the ROI, small, and returns
 * the gray fingerprint. No full-frame copy and no JPEG encode.
 * `detail.reason` is set when the sample is empty: "bad-roi", "no-frame",
 * "tainted" (cross-origin clip), or "unreadable".
 */
export function roiFingerprint(source, roi, detail) {
  const box = normalizeRoi(roi);
  const { w, h } = sourceSize(source);
  if (!box) {
    noteRoiFailure(detail, "bad-roi");
    return null;
  }
  if (w < 2 || h < 2) {
    noteRoiFailure(detail, "no-frame");
    return null;
  }
  const rect = roiSourceRect(box, w, h);
  if (!rect) {
    noteRoiFailure(detail, "bad-roi");
    return null;
  }
  const { sx, sy, sw, sh } = rect;
  const scale = Math.min(1, FINGERPRINT_SAMPLE_MAX_W / sw);
  if (!fingerprintCanvas) fingerprintCanvas = document.createElement("canvas");
  const outW = Math.max(16, Math.round(sw * scale));
  const outH = Math.max(16, Math.round(sh * scale));
  if (fingerprintCanvas.width !== outW) fingerprintCanvas.width = outW;
  if (fingerprintCanvas.height !== outH) fingerprintCanvas.height = outH;
  const ctx = fingerprintCanvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) {
    noteRoiFailure(detail, "unreadable");
    return null;
  }
  try {
    ctx.drawImage(source, sx, sy, sw, sh, 0, 0, outW, outH);
    const pixels = ctx.getImageData(0, 0, outW, outH);
    return fingerprintFromRgba(pixels.data, pixels.width, pixels.height);
  } catch (err) {
    const msg = `${err?.name || ""} ${err?.message || ""}`;
    noteRoiFailure(
      detail,
      /taint|SecurityError/i.test(msg) ? "tainted" : "unreadable"
    );
    return null;
  }
}

/** JPEG of the ROI for Gemini — encode only when the gate decides to read. */
export function roiToJpegBlob(source, roi, { quality = 0.62, maxW = 480 } = {}) {
  const box = normalizeRoi(roi);
  const { w, h } = sourceSize(source);
  if (!box || w < 2 || h < 2) return Promise.resolve(null);
  const rect = roiSourceRect(box, w, h);
  if (!rect) return Promise.resolve(null);
  const { sx, sy, sw, sh } = rect;
  const scale = Math.min(1, maxW / sw);
  const out = document.createElement("canvas");
  out.width = Math.max(16, Math.round(sw * scale));
  out.height = Math.max(16, Math.round(sh * scale));
  const ctx = out.getContext("2d");
  if (!ctx) return Promise.resolve(null);
  try {
    ctx.drawImage(source, sx, sy, sw, sh, 0, 0, out.width, out.height);
  } catch {
    return Promise.resolve(null);
  }
  return safeCanvasToJpegBlob(out, quality);
}

export function videoRoiToJpegBase64(video, roi, { quality = 0.72, maxW = 480 } = {}) {
  return videoRoiToScoreFrame(video, roi, { quality, maxW })
    .then((frame) => (frame?.blob ? blobToJpegBase64(frame.blob) : ""))
    .catch(() => "");
}

export function blobToJpegBase64(blob) {
  if (!blob) return Promise.resolve("");
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = String(reader.result || "");
      const i = s.indexOf("base64,");
      resolve(i >= 0 ? s.slice(i + 7) : s);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

export function isFreshBoardScore(reading, now = Date.now(), maxAgeMs = 45_000) {
  if (!reading || reading.visible !== true) return false;
  if (reading.board_left == null || reading.board_right == null) return false;
  const at = Date.parse(reading.updated_at || "");
  if (!Number.isFinite(at)) return true;
  return now - at >= 0 && now - at <= maxAgeMs;
}

export function scoreTimelineAt(timeline, tMs) {
  if (!Array.isArray(timeline) || !timeline.length) return null;
  const t = Number(tMs);
  if (!Number.isFinite(t)) return timeline[timeline.length - 1] || null;
  let best = null;
  for (const row of timeline) {
    const ts = Number(row?.t_ms);
    if (!Number.isFinite(ts) || ts > t) continue;
    if (!best || ts >= Number(best.t_ms)) best = row;
  }
  return best;
}
